// js/flatpickr-init.js
// Initialise Flatpickr sur les champs de date/heure du formulaire si présents

import {
    buildBookedDays,
    getFirstFreeSlot,
    parseDateTimezone,
    checkDateRangeOverlap,
} from './gemengApp/shared/bookingSlots.js';
import { ensureRentableRange } from './gemengApp/shared/apiClient.js';
import { bookingHorizonEnd } from './gemengApp/shared/dateHelpers.js';
import { t } from './gemengApp/shared/labels.js';
import {
    JOURS_LU,
    JOURS_LU_COURTS,
    MOIS_LU,
    MOIS_LU_COURTS,
    langueDeLaPage,
} from './gemengApp/shared/dateHelpers.js';

/**
 * Locale des calendriers, d'après la langue de la page.
 *
 * Le français était chargé et appliqué inconditionnellement, sur les huit
 * instanciations : un visiteur de /lu/ lisait « lundi mardi mercredi » et « juin »
 * dans un calendrier au milieu d'une page luxembourgeoise. Flatpickr ne publie
 * aucune locale `lb`, on la construit donc à partir des noms déjà écrits dans
 * `dateHelpers.js` — les mêmes qui servent au récapitulatif de l'étape 4.
 */
const LOCALE_LU = {
    weekdays: { shorthand: JOURS_LU_COURTS, longhand: JOURS_LU },
    months: { shorthand: MOIS_LU_COURTS, longhand: MOIS_LU },
    firstDayOfWeek: 1,
    rangeSeparator: ' au ',
    weekAbbreviation: 'Sem',
    scrollTitle: 'Scrollen fir ze erhéijen',
    toggleTitle: 'Klicken fir ze wiesselen',
    time_24hr: true,
};

// Locales publiées par flatpickr que ce site peut réellement servir. Le
// luxembourgeois n'en fait pas partie : il est monté à la main ci-dessus.
const LOCALES_CDN = { fr: 'fr', de: 'de' };

const LANGUE_PAGE = langueDeLaPage();
// Nom passé à chaque instanciation. `default` = les libellés anglais intégrés, que
// l'on n'utilise que si la langue de la page n'a ni locale CDN ni table maison.
let localeFlatpickr = LOCALES_CDN[LANGUE_PAGE] || (LANGUE_PAGE === 'lu' ? 'lu' : 'default');

// Horizon de récupération ET de sélection des réservations. Les dates au-delà ne
// peuvent pas être validées (les jours réservés ne sont pas chargés), donc on borne
// aussi le `maxDate` des pickers pour éviter une réservation en conflit non détectée.
// La borne elle-même vit dans dateHelpers.js : la fiche salle s'aligne dessus.

/** "jj/mm/aaaa" — l'année est indispensable : on réserve jusqu'à 3 ans en avance. */
function jourMoisAnnee(date) {
    return (
        date.getDate().toString().padStart(2, "0") +
        "/" +
        (date.getMonth() + 1).toString().padStart(2, "0") +
        "/" +
        date.getFullYear()
    );
}

/** Libellé affiché dans les champs de plage. Une journée seule se lit "Du X au X". */
function libelleDuAu(debut, fin) {
    return `Du ${jourMoisAnnee(debut)} au ${jourMoisAnnee(fin)}`;
}

// Rentable « Open Air / Autre » : on y réserve du matériel livré, pas une salle.
// Il déroge à toute la mécanique de dates (aucun jour bloqué, pas de contrôle de
// chevauchement, pas de période de montage/démontage). L'UUID était recopié à
// chaque endroit ; il n'existe plus qu'ici côté JS.
const OPEN_AIR_RENTABLE_ID = '1d958e11-2704-45d3-84eb-125f5dbb0b14';

function isOpenAirRentable() {
    return document.querySelector('input[name="rentableId"]')?.value === OPEN_AIR_RENTABLE_ID;
}

/**
 * Le bloc « montage/démontage » n'est pas affiché pour Open Air, mais le serveur
 * exige toujours rentStart/rentEnd/rentStartTime/rentEndTime. On les tient donc
 * alignés sur la période de l'événement — la seule que l'usager saisit.
 *
 * On se branche sur l'évènement `change` des champs cachés « event », que tous
 * les chemins de mise à jour émettent déjà (pickers de dates ET pickers d'heures).
 */
function setupOpenAirRentMirroring() {
    if (!isOpenAirRentable()) return;

    const pairs = [
        ["eventStartDate", "rentStartDate"],
        ["eventEndDate", "rentEndDate"],
        ["eventStartTime", "rentStartTime"],
        ["eventEndTime", "rentEndTime"],
    ];

    const mirror = function () {
        pairs.forEach(function ([sourceId, targetId]) {
            const source = document.getElementById(sourceId);
            const target = document.getElementById(targetId);
            // Pas de dispatch sur la cible : rien n'écoute les champs « rent »
            // pour Open Air (la validation de chevauchement est court-circuitée),
            // et ça évite toute boucle d'évènements.
            if (source && target && target.value !== source.value) {
                target.value = source.value;
            }
        });
    };

    pairs.forEach(function ([sourceId]) {
        const source = document.getElementById(sourceId);
        if (source) source.addEventListener("change", mirror);
    });

    // État initial (dates pré-remplies depuis la fiche salle ou un POST refusé).
    mirror();
}

let rangeEventPickr;

document.addEventListener("DOMContentLoaded", function () {
    setupOpenAirRentMirroring();

    // Ajouter un indicateur de chargement visuel
    function showFlatpickrLoading() {
        const dateFields = document.querySelectorAll(
            'input[type="text"][name*="Date"], input[type="text"][name*="Time"]'
        );
        dateFields.forEach((field) => {
            if (!field.closest(".loading-flatpickr")) {
                const wrapper = document.createElement("div");
                wrapper.className = "loading-flatpickr position-relative";
                field.parentNode.insertBefore(wrapper, field);
                wrapper.appendChild(field);

                const loader = document.createElement("div");
                loader.className = "flatpickr-loader position-absolute";
                loader.style.cssText = `
                    top: 50%; right: 10px; transform: translateY(-50%);
                    width: 16px; height: 16px;
                    border: 2px solid #f3f3f3;
                    border-top: 2px solid #007bff;
                    border-radius: 50%;
                    animation: flatpickr-spin 1s linear infinite;
                    z-index: 10;
                `;
                wrapper.appendChild(loader);

                field.style.paddingRight = "35px";
                field.disabled = true;
            }
        });

        // Ajouter les styles CSS pour l'animation et les jours désactivés
        if (!document.querySelector("#flatpickr-loader-styles")) {
            const style = document.createElement("style");
            style.id = "flatpickr-loader-styles";
            style.textContent = `
                @keyframes flatpickr-spin {
                    0% { transform: translateY(-50%) rotate(0deg); }
                    100% { transform: translateY(-50%) rotate(360deg); }
                }
                
                /* Style pour les jours J à J+4 désactivés */
                .flatpickr-day.flatpickr-disabled.j-plus-disabled {
                    color: rgba(0, 0, 0, 0.30)!important;
                    text-decoration: line-through;
                    cursor: not-allowed !important;
                }
                
                /* Style pour les jours partiellement réservés (<8h) - couleur orange sur le texte */
                .flatpickr-day.partial-booked-text {
                    color: #fd7e14 !important;
                    font-weight: 700 !important;
                }
                
                /* Style pour les jours complets réservés (+8h) - couleur rouge sur le texte */
                .flatpickr-day.full-booked-text {
                    color: #dc3545 !important;
                    font-weight: 700 !important;
                    text-decoration: line-through;
                }
                
                /* Style pour les champs form-control désactivés */
                .form-control:disabled {
                    background-color: #f8f9fa !important;
                    border-color: #dee2e6 !important;
                    opacity: 0.65 !important;
                    cursor: not-allowed !important;
                    color: #6c757d !important;
                }
                
                /* Forcer l'affichage des tooltips sur boutons désactivés */
                .next-step.disabled.has-tooltip,
                .next-step[disabled].has-tooltip {
                    pointer-events: auto !important;
                    cursor: not-allowed !important;
                }
                
                /* Style pour les boutons désactivés avec tooltip */
                .next-step.disabled.has-tooltip:hover,
                .next-step[disabled].has-tooltip:hover {
                    opacity: 0.5 !important;
                    cursor: not-allowed !important;
                }
                
                /* Tooltip CSS custom pour garantir l'affichage */
                .next-step.has-tooltip {
                    position: relative;
                }
                
                .next-step.has-tooltip:hover::after {
                    content: attr(data-tooltip);
                    position: absolute;
                    bottom: calc(100% + 5px);
                    left: 50%;
                    transform: translateX(-50%);
                    background-color: #333;
                    color: white;
                    padding: 8px 12px;
                    border-radius: 4px;
                    font-size: 12px;
                    white-space: nowrap;
                    z-index: 1000;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                }
                
                .next-step.has-tooltip:hover::before {
                    content: '';
                    position: absolute;
                    bottom: calc(100% - 1px);
                    left: 50%;
                    transform: translateX(-50%);
                    border: 5px solid transparent;
                    border-top-color: #333;
                    z-index: 1000;
                }
                
                /* Style par défaut pour les champs event non utilisables */
                input[name="eventStartDate"]:disabled,
                input[name="eventEndDate"]:disabled,
                #eventStartDate:disabled,
                #eventEndDate:disabled {
                    background-color: #f8f9fa !important;
                    border-color: #dee2e6 !important;
                    opacity: 0.65 !important;
                    cursor: not-allowed !important;
                    color: #6c757d !important;
                }
            `;
            document.head.appendChild(style);
        }
    }

    function hideFlatpickrLoading() {
        const isAppointmentForm =
            document.querySelector("form.js-form-push") !== null;

        document
            .querySelectorAll(".flatpickr-loader")
            .forEach((loader) => loader.remove());
        document.querySelectorAll(".loading-flatpickr input").forEach((field) => {
            field.style.paddingRight = "";

            // Keep hidden fields always hidden and disabled
            const hiddenFields = [
                "rentStartDate",
                "rentEndDate",
                "rentStartTime",
                "rentEndTime",
                "eventStartDate",
                "eventEndDate",
                "eventStartTime",
                "eventEndTime",
            ];
            if (field.name && hiddenFields.includes(field.name)) {
                // These fields should stay hidden and disabled
                return;
            }

            // Don't re-enable event fields - they should stay disabled until rent dates are valid
            // But for appointment forms, don't touch event fields at all
            if (isAppointmentForm) {
                // For appointment forms, re-enable all non-event fields
                if (
                    !field.name ||
                    (!field.name.includes("eventStartDate") &&
                        !field.name.includes("eventEndDate") &&
                        !field.name.includes("eventStartTime") &&
                        !field.name.includes("eventEndTime"))
                ) {
                    field.disabled = false;
                }
            } else {
                // For room booking forms, keep event fields disabled until rent dates are valid
                if (
                    !field.name ||
                    (!field.name.includes("eventStartDate") &&
                        !field.name.includes("eventEndDate") &&
                        !field.name.includes("eventStartTime") &&
                        !field.name.includes("eventEndTime"))
                ) {
                    field.disabled = false;
                }
            }
        });
    }

    // Vérifie si Flatpickr est déjà chargé
    function loadFlatpickr(callback) {
        if (window.flatpickr) {
            callback();
            return;
        }

        // Précharger le CSS immédiatement (non-bloquant)
        var css = document.createElement("link");
        css.rel = "stylesheet";
        css.href = "https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css";
        document.head.appendChild(css);

        // Charger le script principal avec timeout pour éviter les blocages
        var script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/flatpickr";
        script.onload = function() {
            // Le luxembourgeois n'existe pas au catalogue flatpickr : on l'enregistre
            // à la main plutôt que d'aller chercher un fichier qui rendra un 404.
            if (LANGUE_PAGE === 'lu') {
                if (window.flatpickr) {
                    window.flatpickr.l10ns = window.flatpickr.l10ns || {};
                    window.flatpickr.l10ns.lu = LOCALE_LU;
                    window.flatpickr.localize(LOCALE_LU);
                }
                callback();
                return;
            }

            var fichierLocale = LOCALES_CDN[LANGUE_PAGE];
            if (!fichierLocale) {
                callback();
                return;
            }

            var localeScript = document.createElement("script");
            localeScript.src =
                "https://cdn.jsdelivr.net/npm/flatpickr/dist/l10n/" + fichierLocale + ".js";
            localeScript.onload = function() {
                if (window.flatpickr && window.flatpickr.l10ns && window.flatpickr.l10ns[fichierLocale]) {
                    window.flatpickr.localize(window.flatpickr.l10ns[fichierLocale]);
                } else {
                    // Locale annoncée mais absente de la réponse : on retombe sur les
                    // libellés intégrés plutôt que de laisser un nom de locale inconnu.
                    localeFlatpickr = 'default';
                }
                callback();
            };
            localeScript.onerror = function() {
                // Le CDN n'a pas répondu : ne pas demander ensuite une locale qui
                // n'existe pas dans `l10ns`, flatpickr lèverait à l'instanciation.
                localeFlatpickr = 'default';
                callback();
            };
            document.head.appendChild(localeScript);
        };
        script.onerror = function () {
            // console.warn('Failed to load Flatpickr from CDN, falling back to basic date inputs');
            hideFlatpickrLoading();
        };

        // Timeout de sécurité
        setTimeout(function () {
            if (!window.flatpickr) {
                // console.warn('Flatpickr loading timeout, proceeding with basic inputs');
                hideFlatpickrLoading();
            }
        }, 5000);

        document.head.appendChild(script);
    }

    function getBookedDays() {
        // À adapter selon la source des données (FullCalendar ou API)
        // Exemple : window.bookedDays = { full: ["2025-08-10", "2025-08-12"], partial: ["2025-08-11"] }
        if (window.bookedDays) {
            return window.bookedDays;
        }
        return { full: [], partial: [] };
    }

    function fetchBookedDays(callback) {
        // Utiliser l'API proxy locale pour éviter CORS
        let rentableId = document.querySelector('input[name="rentableId"]')?.value;

        // For place.njk, try to get from eventStartDate input or fullcalendar
        if (!rentableId) {
            const eventStartInput = document.getElementById("eventStartDate");
            if (eventStartInput) {
                rentableId = eventStartInput.dataset.rentableid;
            }
        }

        if (!rentableId) {
            const fullCalendar = document.getElementById("fullCalendarGemengApp");
            if (fullCalendar) {
                rentableId = fullCalendar.dataset.rentableid;
            }
        }

        // console.log('Found rentableId:', rentableId);

        if (!rentableId) {
            // console.warn('⚠️ Warning: rentableId is empty or not found. Using empty booked days array.');
            callback({ full: [], partial: [] });
            return;
        }

        // Récupérer toute la fenêtre sélectionnable (BOOKING_HORIZON_YEARS), sinon
        // les réservations au-delà de ~3 mois seraient invisibles au contrôle d'overlap.
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = bookingHorizonEnd();

        // console.log(
        //   "Fetching booked days for rentableId:",
        //   rentableId,
        //   "period:",
        //   start,
        //   "to",
        //   end,
        //   "link:",
        //   `/api-rentable?rentable=${rentableId}&start=${start}&end=${end}`
        // );

        // ⚠️ Ici, contrairement au calendrier de la fiche, on charge d'emblée **tout le
        // domaine sélectionnable** (minDate J+N → maxDate = bookingHorizonEnd), et non la
        // seule plage affichée. Ce n'est pas un oubli d'optimisation : ces données ne
        // servent pas qu'à colorer, elles alimentent `disable` et surtout
        // `validateDateRangeOverlaps`, qui est **synchrone**. Ne charger que le mois
        // affiché laisserait un usager sélectionner une plage débordant sur un mois jamais
        // chargé, et le conflit passerait inaperçu jusqu'au refus du serveur.
        // Le cache d'apiClient est partagé avec le calendrier : rien n'est demandé deux fois.
        ensureRentableRange(rentableId, start, end)
            .then((data) => {
                // Classification déléguée à bookingSlots : un jour n'est "full"
                // que s'il est réellement saturé (sinon il reste partiel et
                // sélectionnable, le contrôle horaire faisant foi).
                callback(buildBookedDays(data));
            })
            .catch(() => {
                callback({ full: [], partial: [] });
            });
    }

    function initFlatpickrFields() {
        var dateFields = [
            "rentStartDate",
            "rentEndDate",
            "eventStartDate",
            "eventEndDate",
            "dateSigned", // champ confirmation
        ];
        var timeFields = [
            "rentStartTime",
            "rentEndTime",
            "eventStartTime",
            "eventEndTime",
        ];

        var booked = getBookedDays();

        // Check if current rentable is Open Air / Autre (should not block dates, only materials)
        let rentableId = document.querySelector('input[name="rentableId"]')?.value;
        const isOpenAirRentable = rentableId === OPEN_AIR_RENTABLE_ID;

        // For Open Air, don't disable any dates
        var disableDates = isOpenAirRentable ? [] : booked.full;
        var partialDates = booked.partial;

        dateFields.forEach(function (fieldName) {
            var el =
                document.getElementById(fieldName) ||
                document.querySelector('input[name="' + fieldName + '"]');
            if (el) {
                // Ne pas initialiser si la valeur est invalide
                if (el.value === "Invalid date" || el.value === "Invalid time") {
                    el.value = "";
                }
                flatpickr(el, {
                    dateFormat: "d-m-Y",
                    allowInput: true,
                    locale: localeFlatpickr,
                    minDate: minSelectableDate, // Enforce J+minDayBooking constraint
                    disable: disableDates,
                    onDayCreate: function (dObj, dStr, fp, dayElem) {
                        var date = dayElem.dateObj;
                        var y = date.getFullYear();
                        var m = (date.getMonth() + 1).toString().padStart(2, "0");
                        var d = date.getDate().toString().padStart(2, "0");
                        var dateStr = y + "-" + m + "-" + d;
                        if (partialDates.includes(dateStr)) {
                            dayElem.classList.add("partial-booked-text");
                            dayElem.title = "Jour partiellement réservé";
                        }
                    },
                });
            }
        });
        timeFields.forEach(function (fieldName) {
            var el =
                document.getElementById(fieldName) ||
                document.querySelector('input[name="' + fieldName + '"]');
            if (el) {
                // Ne pas initialiser si la valeur est invalide
                if (el.value === "Invalid date" || el.value === "Invalid time") {
                    el.value = "";
                }
                flatpickr(el, {
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: "H:i",
                    time_24hr: true,
                    allowInput: true,
                    locale: localeFlatpickr,
                });
            }
        });
    }

    function initFlatpickrFieldsWithBookedDays(booked) {
        // Check if we're in an appointment form context
        const isAppointmentForm =
            document.querySelector(".form-appointment") !== null;
        const isBookingForm = document.querySelector(".form-bookingapp") !== null;
        // console.log('🏢 Form detection:', { isAppointmentForm, isBookingForm });

        var dateFields = [
            "dateSigned", // Only dateSigned needs flatpickr, others are hidden inputs
        ];
        var timeFields = [
            // No time fields need flatpickr initialization - all managed by range pickers
        ];

        // Add date range fields
        var dateRangeFields = ["rentDateRange"];
        var eventDateRangeFields = ["eventDateRange"];

        // Set today's date as default for dateSigned field and make it readonly
        const dateSignedField =
            document.getElementById("dateSigned") ||
            document.querySelector('input[name="custom.dateSigned"]');
        if (dateSignedField) {
            const today = new Date();
            const todayFormatted =
                today.getDate().toString().padStart(2, "0") +
                "-" +
                (today.getMonth() + 1).toString().padStart(2, "0") +
                "-" +
                today.getFullYear();
            dateSignedField.value = todayFormatted;
            dateSignedField.readOnly = true;
            // console.log('Set default today date for dateSigned:', todayFormatted);
        }

        // Check if current rentable is Open Air / Autre (should not block dates, only materials)
        let rentableId = document.querySelector('input[name="rentableId"]')?.value;
        const isOpenAirRentable = rentableId === OPEN_AIR_RENTABLE_ID;
        // console.log('🏟️ Open Air check:', { rentableId, isOpenAirRentable, fullBookedDays: booked.full.length });

        // Jours désactivés = uniquement les jours réellement saturés (booked.full).
        // Les jours partiels restent sélectionnables : le contrôle de chevauchement
        // horaire (validateDateRangeOverlaps) bloque les vrais conflits, ce qui permet
        // une 2e réservation le même jour sur un créneau libre.
        // Skip date blocking for Open Air rentable.
        var disableDates = isOpenAirRentable ? [] : [...booked.full];
        var partialDates = booked.partial.map((p) => p.date);

        // Remove duplicates
        disableDates = [...new Set(disableDates)];

        let disableDatesFormatted = disableDates.map((date) => {
            return new Date(date);
        });

        // Get minDayBooking - check for appointment calendar first, then hall booking calendar
        const appointmentCalendarEl = document.querySelector(
            "#fullCalendarAppointment"
        );
        const hallBookingCalendarEl = document.querySelector(
            "#fullCalendarGemengApp"
        );

        // Délai minimum de réservation. Il vient de GMS (`rentable_data.minDayBooking`)
        // et varie d'une salle à l'autre ; il était figé à 4 ici. Sur une salle qui
        // exige 10 jours, la fiche barrait bien J..J+9 et la légende du formulaire
        // annonçait « 10 jours », mais les pickers ne barraient que J..J+3 : l'usager
        // sélectionnait J+6, le formulaire l'acceptait, et la demande partait en
        // violation du délai. Le gabarit expose maintenant la valeur sur le bloc
        // (`data-mindaybooking` sur `.form-bookingapp`), à côté de celle que porte
        // déjà le calendrier de la fiche.
        const formBookingEl = document.querySelector(".form-bookingapp");
        const appointmentFormEl = document.querySelector(".js-form-appointment-booking");

        let minDayBooking = 4; // repli, aligné sur celui de form-booking-app.njk

        if (appointmentFormEl) {
            // Rendez-vous : J+3 par défaut, ou la valeur portée par le formulaire.
            minDayBooking = parseInt(appointmentFormEl.dataset.mindaybooking || 3, 10);
        } else if (formBookingEl && formBookingEl.dataset.mindaybooking) {
            minDayBooking = parseInt(formBookingEl.dataset.mindaybooking, 10);
        } else if (hallBookingCalendarEl && hallBookingCalendarEl.dataset.mindaybooking) {
            // Fiche salle : la valeur est sur le calendrier.
            minDayBooking = parseInt(hallBookingCalendarEl.dataset.mindaybooking, 10);
        }
        if (isNaN(minDayBooking) || minDayBooking < 0) minDayBooking = 4;

        const today = new Date();
        // Enforce J+minDayBooking constraint - no reservations before this date
        const minSelectableDate = new Date(today);
        minSelectableDate.setDate(today.getDate() + minDayBooking);
        // Ramené à MINUIT, comme le fait `getMinBookingDate()` du calendrier de la
        // fiche (_calendar.js). Sans cela, `minSelectableDate` valait « J+4 à 14:37 »,
        // et `onDayCreate` — qui compare des cellules ramenées à minuit — barrait le
        // jour J+4 tout entier : le calendrier de la fiche le présentait libre, le
        // formulaire le refusait en silence, et l'info-bulle annonçait pourtant
        // « impossible AVANT J+4 ». Un jour d'écart entre les deux écrans.
        minSelectableDate.setHours(0, 0, 0, 0);
        // Borne haute = horizon de récupération des jours réservés : on ne peut pas
        // valider l'overlap au-delà (données non chargées).
        const maxSelectableDate = bookingHorizonEnd();

        // console.log('Flatpickr booked.full:', disableDates);
        // console.log('Flatpickr booked.partial:', partialDates);
        // console.log('Flatpickr dateFields:', dateFields);
        // console.log('Flatpickr timeFields:', timeFields);

        // Debug: Check if we're on place.njk
        const isPlacePage =
            document.getElementById("fullCalendarGemengApp") !== null;
        // console.log('Is place page:', isPlacePage);
        // console.log('Processing flatpickr with booked data:', booked);

        // Debug: Check if we have eventStart/eventEnd date inputs on place.njk
        if (isPlacePage) {
            const eventStartInput = document.getElementById("eventStartDate");
            const eventEndInput = document.getElementById("eventEndDate");
            // console.log('Place page eventStartDate input:', eventStartInput);
            // console.log('Place page eventEndDate input:', eventEndInput);
            if (eventStartInput) {
                // console.log('eventStartDate data-rentableid:', eventStartInput.dataset.rentableid);
            }
            if (eventEndInput) {
                // console.log('eventEndDate data-rentableid:', eventEndInput.dataset.rentableid);
            }
        }

        // Helper: update hidden rent date/time fields from a date range
        function updateRentHiddenFields(startDate, endDate) {
            const rentStartEl =
                document.getElementById("rentStartDate") ||
                document.querySelector('input[name="rentStartDate"]');
            const rentEndEl =
                document.getElementById("rentEndDate") ||
                document.querySelector('input[name="rentEndDate"]');
            const rentStartTimeEl =
                document.getElementById("rentStartTime") ||
                document.querySelector('input[name="rentStartTime"]');
            const rentEndTimeEl =
                document.getElementById("rentEndTime") ||
                document.querySelector('input[name="rentEndTime"]');

            const formatTime = (d) =>
                d.getHours().toString().padStart(2, "0") +
                ":" +
                d.getMinutes().toString().padStart(2, "0");

            if (rentStartEl) {
                rentStartEl.value = formatDateISO(startDate);
                rentStartEl.dispatchEvent(new Event("change", { bubbles: true }));
            }
            if (rentEndEl) {
                rentEndEl.value = formatDateISO(endDate);
                rentEndEl.dispatchEvent(new Event("change", { bubbles: true }));
            }
            if (rentStartTimeEl) {
                // Use visible time picker value as fallback if date has 00:00
                const visibleStart = document.querySelector('input[name="visibleRentStartTime"]');
                const timeVal = (startDate.getHours() === 0 && startDate.getMinutes() === 0 && visibleStart && visibleStart.value)
                    ? visibleStart.value
                    : formatTime(startDate);
                rentStartTimeEl.value = timeVal;
                rentStartTimeEl.dispatchEvent(new Event("change", { bubbles: true }));
            }
            if (rentEndTimeEl) {
                // Use visible time picker value as fallback if date has 00:00
                const visibleEnd = document.querySelector('input[name="visibleRentEndTime"]');
                const timeVal = (endDate.getHours() === 0 && endDate.getMinutes() === 0 && visibleEnd && visibleEnd.value)
                    ? visibleEnd.value
                    : formatTime(endDate);
                rentEndTimeEl.value = timeVal;
                rentEndTimeEl.dispatchEvent(new Event("change", { bubbles: true }));
            }
        }

        // Helper: update hidden event date/time fields from a date range
        function updateEventHiddenFields(startDate, endDate, includeTime) {
            const eventStartEl =
                document.getElementById("eventStartDate") ||
                document.querySelector('input[name="eventStartDate"]');
            const eventEndEl =
                document.getElementById("eventEndDate") ||
                document.querySelector('input[name="eventEndDate"]');

            const formatTime = (d) =>
                d.getHours().toString().padStart(2, "0") +
                ":" +
                d.getMinutes().toString().padStart(2, "0");

            if (eventStartEl) {
                eventStartEl.value = formatDateISO(startDate);
                eventStartEl.dispatchEvent(new Event("change", { bubbles: true }));
            }
            if (eventEndEl) {
                eventEndEl.value = formatDateISO(endDate);
                eventEndEl.dispatchEvent(new Event("change", { bubbles: true }));
            }

            if (includeTime) {
                const eventStartTimeEl =
                    document.getElementById("eventStartTime") ||
                    document.querySelector('input[name="eventStartTime"]');
                const eventEndTimeEl =
                    document.getElementById("eventEndTime") ||
                    document.querySelector('input[name="eventEndTime"]');

                if (eventStartTimeEl) {
                    const visibleStart = document.querySelector('input[name="visibleEventStartTime"]');
                    const timeVal = (startDate.getHours() === 0 && startDate.getMinutes() === 0 && visibleStart && visibleStart.value)
                        ? visibleStart.value
                        : formatTime(startDate);
                    eventStartTimeEl.value = timeVal;
                    eventStartTimeEl.dispatchEvent(new Event("change", { bubbles: true }));
                }
                if (eventEndTimeEl) {
                    const visibleEnd = document.querySelector('input[name="visibleEventEndTime"]');
                    const timeVal = (endDate.getHours() === 0 && endDate.getMinutes() === 0 && visibleEnd && visibleEnd.value)
                        ? visibleEnd.value
                        : formatTime(endDate);
                    eventEndTimeEl.value = timeVal;
                    eventEndTimeEl.dispatchEvent(new Event("change", { bubbles: true }));
                }
            }
        }

        // Réhydratation : reporte sur les objets Date les heures déjà présentes dans les
        // champs cachés (pré-remplis par le template depuis les paramètres d'URL, p. ex. le
        // créneau 14:00-18:00 cliqué dans FullCalendar).
        //
        // Indispensable AVANT setDate(..., true) : le onChange des pickers remplace toute
        // heure à minuit par 08:00/23:00, puis réécrit les champs cachés — l'heure choisie
        // par l'utilisateur serait donc écrasée. En posant les heures ici, les dates ne sont
        // plus à minuit et le onChange les laisse tranquilles.
        function applyPrefilledTimes(startDate, endDate, isRent) {
            // Neutralise le pré-remplissage « premier créneau libre » pour CE jour.
            //
            // `applyFreeSlotTimes` s'exécute une fois par jour nouvellement choisi, via
            // le `onChange` que déclenche le `setDate(..., true)` de la réhydratation.
            // Il passait donc juste APRÈS cette fonction et réécrivait les heures
            // qu'elle venait de restaurer : un créneau 14:00-18:00 cliqué dans le
            // calendrier de la fiche, ou repeuplé au retour d'un POST refusé, se
            // retrouvait ramené au premier créneau libre de la journée. En déclarant
            // le jour déjà traité, on lui laisse sa raison d'être — aider quand
            // l'usager choisit une date sans heure — sans lui laisser écraser un
            // choix explicite.
            lastFreeSlotDay[isRent ? "rent" : "event"] = formatDateISO(startDate);

            const startEl = document.getElementById(
                isRent ? "rentStartTime" : "eventStartTime"
            );
            const endEl = document.getElementById(
                isRent ? "rentEndTime" : "eventEndTime"
            );

            const setFrom = (date, el, fallbackHour) => {
                const parts = String(el?.value || "").match(/^(\d{1,2}):(\d{2})$/);
                if (parts) {
                    date.setHours(parseInt(parts[1], 10), parseInt(parts[2], 10), 0, 0);
                } else {
                    date.setHours(fallbackHour, 0, 0, 0);
                }
            };

            setFrom(startDate, startEl, 8);
            setFrom(endDate, endEl, 23);
        }

        /**
         * Applique à une plage les heures que l'usager a choisies.
         *
         * Remplace un `if (getHours() === 0) setHours(8)` / `setHours(23)` écrit en
         * dur à quatre endroits. Le calendrier rend toujours ses dates à minuit :
         * la règle « minuit ? alors 08:00 » ne distinguait donc pas « l'usager n'a
         * pas encore choisi d'heure » de « l'usager vient de changer les dates ».
         * Conséquence mesurable : régler la manifestation de 14:00 à 18:00 puis
         * corriger le jour remettait 08:00–23:00 dans les champs cachés — le
         * `updateRentHiddenFields`/`updateEventHiddenFields` qui suit voyait alors
         * 8 h et non minuit, donc son propre repli sur le champ visible ne jouait
         * jamais. La salle partait bloquée toute la journée, sans un mot à l'écran.
         *
         * On lit donc le champ d'heure VISIBLE, celui que l'usager manipule, et on
         * ne retombe sur 08:00 / 23:00 que lorsqu'il est vide ou illisible.
         */
        function appliquerHeuresChoisies(startDate, endDate, isRent) {
            const lire = (nom, repli) => {
                const el = document.querySelector(`input[name="${nom}"]`);
                const parts = String(el?.value || '').match(/^(\d{1,2}):(\d{2})$/);
                if (!parts) return [repli, 0];
                return [parseInt(parts[1], 10), parseInt(parts[2], 10)];
            };

            const [sh, sm] = lire(isRent ? 'visibleRentStartTime' : 'visibleEventStartTime', 8);
            const [eh, em] = lire(isRent ? 'visibleRentEndTime' : 'visibleEventEndTime', 23);

            if (startDate.getHours() === 0 && startDate.getMinutes() === 0) {
                startDate.setHours(sh, sm, 0, 0);
            }
            if (endDate.getHours() === 0 && endDate.getMinutes() === 0) {
                endDate.setHours(eh, em, 0, 0);
            }
        }

        // Dernier jour pour lequel on a pré-rempli les heures, par picker. Sert à ne pas
        // réécraser des heures que l'utilisateur a ajustées à la main ensuite (cf. plus bas).
        const lastFreeSlotDay = { rent: null, event: null };

        // Pré-remplit les heures sur le PREMIER CRÉNEAU LIBRE quand l'utilisateur
        // sélectionne un jour (mono-jour) déjà partiellement réservé, afin de ne pas
        // atterrir directement sur une erreur de chevauchement. Modifie les objets Date
        // en place et synchronise les time pickers visibles. Ne fait rien si Open Air,
        // si multi-jours, ou si le jour n'a aucune réservation existante.
        function applyFreeSlotTimes(startDate, endDate, isRent) {
            if (isOpenAirRentable) return;
            const sameDay =
                startDate.getFullYear() === endDate.getFullYear() &&
                startDate.getMonth() === endDate.getMonth() &&
                startDate.getDate() === endDate.getDate();
            if (!sameDay) return;

            const dateStr = formatDateISO(startDate);

            if (!booked.partial.some((p) => p.date === dateStr)) return;

            // Ne pré-remplir qu'UNE fois par jour sélectionné : re-cliquer la même date
            // (pour confirmer la plage, par exemple) ne doit pas réinitialiser des heures
            // que l'utilisateur a réglées entre-temps sur un autre créneau libre valide.
            const key = isRent ? "rent" : "event";
            if (lastFreeSlotDay[key] === dateStr) return;
            lastFreeSlotDay[key] = dateStr;

            const slot = getFirstFreeSlot(dateStr, booked);
            const [sh, sm] = slot.startTime.split(":").map(Number);
            const [eh, em] = slot.endTime.split(":").map(Number);

            startDate.setHours(sh, sm, 0, 0);
            endDate.setHours(eh, em, 0, 0);

            // Synchronise les time pickers visibles.
            const startName = isRent ? "visibleRentStartTime" : "visibleEventStartTime";
            const endName = isRent ? "visibleRentEndTime" : "visibleEventEndTime";
            const vs = document.querySelector(`input[name="${startName}"]`);
            const ve = document.querySelector(`input[name="${endName}"]`);
            if (vs) {
                vs.value = slot.startTime;
                if (vs._flatpickr) vs._flatpickr.setDate(slot.startTime, false);
            }
            if (ve) {
                ve.value = slot.endTime;
                if (ve._flatpickr) ve._flatpickr.setDate(slot.endTime, false);
            }
        }

        // Initialize date range picker first
        dateRangeFields.forEach(function (fieldName) {
            var el =
                document.getElementById(fieldName) ||
                document.querySelector('input[name="' + fieldName + '"]');
            if (el) {
                // Initialize flatpickr with range mode and time
                let flatpickrInstance = flatpickr(el, {
                    mode: "range",
                    dateFormat: "d/m/Y",
                    // `allowInput: false` : le champ affiche « Du jj/mm/aaaa au jj/mm/aaaa », que
                    // flatpickr ne sait pas relire. La saisie clavier lui ferait vider la sélection.
                    // L'année figure au format depuis qu'on réserve jusqu'à 3 ans en avance : sans
                    // elle, « 12/06 » était illisible et, saisie au clavier, se relisait dans l'année
                    // courante — défaut remonté par la commune, mesuré le 25/08/2026.
                    allowInput: false,
                    locale: localeFlatpickr,
                    minDate: minSelectableDate,
                    maxDate: maxSelectableDate,
                    disable: disableDatesFormatted,
                    conjunction: " au ",
                    formatDate: function (date, format, locale) {
                        return jourMoisAnnee(date);
                    },
                    onValueUpdate: function (selectedDates, dateStr, instance) {
                        if (selectedDates.length === 2) {
                            // Custom display format with "Du"
                            const startDate = selectedDates[0];
                            const endDate = selectedDates[1];
                            const customFormat = libelleDuAu(startDate, endDate);
                            instance.input.value = customFormat;
                        } else if (selectedDates.length === 1) {
                            // Handle single date selection (same day)
                            const singleDate = selectedDates[0];
                            const customFormat = libelleDuAu(singleDate, singleDate);
                            instance.input.value = customFormat;
                        }
                        // Validate date range overlaps immediately
                        setTimeout(() => {
                            validateDateRangeOverlaps();
                            // console.log('📝 Validating rent date overlaps');
                        }, 100);

                    },
                    onDayCreate: function (dObj, dStr, fp, dayElem) {
                        var date = dayElem.dateObj;
                        var y = date.getFullYear();
                        var m = (date.getMonth() + 1).toString().padStart(2, "0");
                        var d = date.getDate().toString().padStart(2, "0");
                        var dateStr = y + "-" + m + "-" + d;

                        // Check if it's a disabled J to J+4 day
                        const currentDate = new Date(date);
                        currentDate.setHours(0, 0, 0, 0);
                        if (currentDate.getTime() < minSelectableDate.getTime()) {
                            dayElem.classList.add("flatpickr-disabled", "j-plus-disabled");
                            dayElem.title = t('jourTropTot', { n: minDayBooking });
                        }

                        // Handle partially booked days - show in red if 8h+ reservation (skip for Open Air)
                        if (!isOpenAirRentable) {
                            // Jour partiellement réservé : reste sélectionnable, coloré en orange,
                            // avec le(s) créneau(x) occupé(s) en info-bulle. Le contrôle horaire
                            // (validateDateRangeOverlaps) bloque uniquement les vrais conflits.
                            const daySlots = booked.partial.filter((p) => p.date === dateStr);
                            if (daySlots.length) {
                                dayElem.classList.add("partial-booked-text");
                                dayElem.title =
                                    t('jourPartiel') +
                                    daySlots.map((s) => `${s.startTime}-${s.endTime}`).join(", ");
                            }
                            // Jour saturé (aucun créneau libre) : déjà non sélectionnable via
                            // `disable`, mais on le distingue visuellement des jours « trop tôt ».
                            if (booked.full.includes(dateStr)) {
                                dayElem.classList.add("full-booked-text");
                                dayElem.title = t('jourComplet');
                            }
                        }
                    },
                    onChange: function (selectedDates, dateStr, instance) {
                        if (selectedDates.length === 2) {
                            appliquerHeuresChoisies(selectedDates[0], selectedDates[1], true);

                            // Heures par défaut = premier créneau libre si le jour
                            // (mono-jour) a déjà une réservation.
                            applyFreeSlotTimes(selectedDates[0], selectedDates[1], true);

                            updateRentHiddenFields(selectedDates[0], selectedDates[1]);

                            // Validate date range overlaps immediately
                            setTimeout(validateDateRangeOverlaps, 100);

                            // Trigger change event on the main range field to update event picker
                            instance.input.dispatchEvent(
                                new Event("change", { bubbles: true })
                            );
                        }
                    },
                    onClose: function (selectedDates, dateStr, instance) {
                        // Handle single date selection (same day) by duplicating it
                        if (selectedDates.length === 1) {
                            const singleDate = selectedDates[0];
                            const endDate = new Date(singleDate);
                            appliquerHeuresChoisies(singleDate, endDate, true);
                            // Heures par défaut = premier créneau libre si le jour a
                            // déjà une réservation.
                            applyFreeSlotTimes(singleDate, endDate, true);
                            instance.setDate([singleDate, endDate], true);
                            // Fix: setDate from onClose doesn't trigger onChange in Flatpickr v4.6.13
                            updateRentHiddenFields(singleDate, endDate);
                            setTimeout(validateDateRangeOverlaps, 100);
                            // Dispatch change event for _booking.js listeners
                            instance.input.dispatchEvent(new Event("change", { bubbles: true }));
                            return;
                        }
                    },
                });

                // Set initial value if both individual dates are already set
                const rentStartEl =
                    document.getElementById("rentStartDate") ||
                    document.querySelector('input[name="rentStartDate"]');
                const rentEndEl =
                    document.getElementById("rentEndDate") ||
                    document.querySelector('input[name="rentEndDate"]');

                if (rentStartEl?.value && rentEndEl?.value) {
                    // Champs cachés au format YYYY-MM-DD -> dates locales pour flatpickr.
                    const startDate = parseISODate(rentStartEl.value);
                    const endDate = parseISODate(rentEndEl.value);

                    if (startDate && endDate) {
                        // Conserver les heures déjà pré-remplies (cf. applyPrefilledTimes).
                        applyPrefilledTimes(startDate, endDate, true);
                        el._flatpickr.setDate([startDate, endDate], true);
                    }

                    // Update display format after setting dates
                    setTimeout(() => {
                        if (el._flatpickr.selectedDates.length === 2) {
                            const startDate = el._flatpickr.selectedDates[0];
                            const endDate = el._flatpickr.selectedDates[1];
                            const customFormat = libelleDuAu(startDate, endDate);
                            el.value = customFormat;
                        }
                    }, 100);
                }
            }
        });

        // Initialize event date range picker (independent)
        // console.log('🎯 Is appointment form?', isAppointmentForm, 'Is booking form?', isBookingForm);
        if (isBookingForm && !isAppointmentForm) {
            // console.log('🎯 Initializing event date range fields:', eventDateRangeFields);
            eventDateRangeFields.forEach(function (fieldName) {
                var el =
                    document.getElementById(fieldName) ||
                    document.querySelector('input[name="' + fieldName + '"]');
                // console.log(`🔍 Looking for event field ${fieldName}:`, el);
                if (el) {
                    // Initialize event date range picker (independent, no rent date constraints)
                    el.disabled = false;
                    el.placeholder = t('placeholderEvenement');

                    rangeEventPickr = flatpickr(el, {
                        mode: "range",
                        dateFormat: "d/m/Y",
                        // `allowInput: false` : le champ affiche « Du jj/mm/aaaa au jj/mm/aaaa », que
                        // flatpickr ne sait pas relire. La saisie clavier lui ferait vider la sélection.
                        // L'année figure au format depuis qu'on réserve jusqu'à 3 ans en avance : sans
                        // elle, « 12/06 » était illisible et, saisie au clavier, se relisait dans l'année
                        // courante — défaut remonté par la commune, mesuré le 25/08/2026.
                        allowInput: false,
                        locale: localeFlatpickr,
                        minDate: minSelectableDate, // Use same J+minDayBooking constraint as rent picker
                        maxDate: maxSelectableDate, // Borne haute = horizon de données chargées
                        disable: isOpenAirRentable ? [] : disableDatesFormatted, // Allow overlap for Open Air, block for others
                        conjunction: " au ",
                        formatDate: function (date, format, locale) {
                            return jourMoisAnnee(date);
                        },
                        onValueUpdate: function (selectedDates, dateStr, instance) {
                            if (selectedDates.length === 2) {
                                // Custom display format with "Du"
                                const startDate = selectedDates[0];
                                const endDate = selectedDates[1];
                                const customFormat = libelleDuAu(startDate, endDate);
                                instance.input.value = customFormat;
                            } else if (selectedDates.length === 1) {
                                // Handle single date selection (same day)
                                const singleDate = selectedDates[0];
                                const customFormat = libelleDuAu(singleDate, singleDate);
                                instance.input.value = customFormat;
                            }

                            // Validate date range overlaps
                            setTimeout(validateDateRangeOverlaps, 100);
                        },
                        onDayCreate: function (dObj, dStr, fp, dayElem) {
                            var date = dayElem.dateObj;
                            var y = date.getFullYear();
                            var m = (date.getMonth() + 1).toString().padStart(2, "0");
                            var d = date.getDate().toString().padStart(2, "0");
                            var dateStr = y + "-" + m + "-" + d;

                            // Check if it's a disabled J to J+4 day
                            const currentDate = new Date(date);
                            currentDate.setHours(0, 0, 0, 0);
                            if (currentDate.getTime() < minSelectableDate.getTime()) {
                                dayElem.classList.add("flatpickr-disabled", "j-plus-disabled");
                                dayElem.title = t('jourTropTot', { n: minDayBooking });
                            }

                            // Handle partially booked days - show in red if 8h+ reservation (skip for Open Air)
                            if (!isOpenAirRentable) {
                                // Jour partiellement réservé : reste sélectionnable, coloré en orange,
                                // avec le(s) créneau(x) occupé(s) en info-bulle. Le contrôle horaire
                                // (validateDateRangeOverlaps) bloque uniquement les vrais conflits.
                                const daySlots = booked.partial.filter((p) => p.date === dateStr);
                                if (daySlots.length) {
                                    dayElem.classList.add("partial-booked-text");
                                    dayElem.title =
                                        t('jourPartiel') +
                                        daySlots.map((s) => `${s.startTime}-${s.endTime}`).join(", ");
                                }
                                // Jour saturé (aucun créneau libre) : déjà non sélectionnable via
                                // `disable`, mais on le distingue visuellement des jours « trop tôt ».
                                if (booked.full.includes(dateStr)) {
                                    dayElem.classList.add("full-booked-text");
                                    dayElem.title = t('jourComplet');
                                }
                            }
                        },
                        onChange: function (selectedDates, dateStr, instance) {
                            if (selectedDates.length === 2) {
                                appliquerHeuresChoisies(selectedDates[0], selectedDates[1], false);

                                // Heures par défaut = premier créneau libre si le jour
                                // (mono-jour) a déjà une réservation.
                                applyFreeSlotTimes(selectedDates[0], selectedDates[1], false);

                                updateEventHiddenFields(selectedDates[0], selectedDates[1], true);

                                // Validate date range overlaps immediately
                                setTimeout(validateDateRangeOverlaps, 100);
                            }
                        },
                        onClose: function (selectedDates, dateStr, instance) {
                            // Handle single date selection (same day) by duplicating it
                            if (selectedDates.length === 1) {
                                const singleDate = selectedDates[0];
                                const endDate = new Date(singleDate);
                                // Set default hours if midnight (same logic as onChange)
                                appliquerHeuresChoisies(singleDate, endDate, false);
                                // Heures par défaut = premier créneau libre si le jour a
                                // déjà une réservation.
                                applyFreeSlotTimes(singleDate, endDate, false);
                                instance.setDate([singleDate, endDate], true);
                                // Fix: setDate from onClose doesn't trigger onChange in Flatpickr v4.6.13
                                updateEventHiddenFields(singleDate, endDate, true);
                                setTimeout(validateDateRangeOverlaps, 100);
                                return;
                            }
                        },
                    });

                    // Autofill rangeEventPickr from pre-filled hidden fields (e.g. from FullCalendar query params)
                    const eventStartEl =
                        document.getElementById("eventStartDate") ||
                        document.querySelector('input[name="eventStartDate"]');
                    const eventEndEl =
                        document.getElementById("eventEndDate") ||
                        document.querySelector('input[name="eventEndDate"]');

                    const startDate = parseISODate(eventStartEl?.value);
                    const endDate = parseISODate(eventEndEl?.value);

                    if (startDate && endDate) {
                        // Conserver le créneau horaire pré-rempli (cf. applyPrefilledTimes) :
                        // sans cela le onChange déclenché par setDate le remplacerait par
                        // 08:00-23:00.
                        applyPrefilledTimes(startDate, endDate, false);
                        rangeEventPickr.setDate([startDate, endDate], true);

                        // Update display format after setting dates
                        setTimeout(() => {
                            if (rangeEventPickr.selectedDates.length === 2) {
                                const s = rangeEventPickr.selectedDates[0];
                                const e = rangeEventPickr.selectedDates[1];
                                const customFormat = libelleDuAu(s, e);
                                el.value = customFormat;
                            }
                        }, 100);
                    }
                } else {
                    // Silently skip - field not present in this form (normal for validation pages)
                    // console.log(`Event field ${fieldName} not found in DOM`);
                }
            });
        }

        // Initialize traffic regulation date range picker (independent, no constraints)
        const trafficDateRangeField = document.getElementById("trafficDateRange") ||
            document.querySelector('input[name="trafficDateRange"]');

        if (trafficDateRangeField) {
            // console.log('✅ Traffic date range field found, initializing flatpickr');

            flatpickr(trafficDateRangeField, {
                mode: "range",
                dateFormat: "d/m/Y",
                // `allowInput: false` : le champ affiche « Du jj/mm/aaaa au jj/mm/aaaa », que
                // flatpickr ne sait pas relire. La saisie clavier lui ferait vider la sélection.
                // L'année figure au format depuis qu'on réserve jusqu'à 3 ans en avance : sans
                // elle, « 12/06 » était illisible et, saisie au clavier, se relisait dans l'année
                // courante — défaut remonté par la commune, mesuré le 25/08/2026.
                allowInput: false,
                locale: localeFlatpickr,
                minDate: "today",
                maxDate: bookingHorizonEnd(), // même horizon que les dates de réservation
                conjunction: " au ",
                formatDate: function (date, format, locale) {
                    return jourMoisAnnee(date);
                },
                onValueUpdate: function (selectedDates, dateStr, instance) {
                    if (selectedDates.length === 2) {
                        const startDate = selectedDates[0];
                        const endDate = selectedDates[1];
                        const customFormat = libelleDuAu(startDate, endDate);
                        instance.input.value = customFormat;
                    } else if (selectedDates.length === 1) {
                        const singleDate = selectedDates[0];
                        const customFormat = libelleDuAu(singleDate, singleDate);
                        instance.input.value = customFormat;
                    }
                },
                onChange: function (selectedDates, dateStr, instance) {
                    if (selectedDates.length === 2) {
                        // Update hidden fields
                        const startDateTime = selectedDates[0];
                        const endDateTime = selectedDates[1];

                        const trafficStartEl = document.getElementById("custom.trafficStartDate") ||
                            document.querySelector('input[name="custom.trafficStartDate"]');
                        const trafficEndEl = document.getElementById("custom.trafficEndDate") ||
                            document.querySelector('input[name="custom.trafficEndDate"]');

                        if (trafficStartEl) {
                            // Format DD/MM/YYYY for display
                            const startFormatted =
                                startDateTime.getDate().toString().padStart(2, "0") +
                                "/" +
                                (startDateTime.getMonth() + 1).toString().padStart(2, "0") +
                                "/" +
                                startDateTime.getFullYear();
                            trafficStartEl.value = startFormatted;
                        }

                        if (trafficEndEl) {
                            // Format DD/MM/YYYY for display
                            const endFormatted =
                                endDateTime.getDate().toString().padStart(2, "0") +
                                "/" +
                                (endDateTime.getMonth() + 1).toString().padStart(2, "0") +
                                "/" +
                                endDateTime.getFullYear();
                            trafficEndEl.value = endFormatted;
                        }
                    }
                },
                onClose: function (selectedDates, dateStr, instance) {
                    // Handle single date selection (same day) by duplicating it
                    if (selectedDates.length === 1) {
                        const singleDate = selectedDates[0];
                        const endDate = new Date(singleDate);
                        instance.setDate([singleDate, endDate], true);

                        // Update hidden traffic date fields (same logic as onChange)
                        const trafficStartEl = document.getElementById("custom.trafficStartDate") ||
                            document.querySelector('input[name="custom.trafficStartDate"]');
                        const trafficEndEl = document.getElementById("custom.trafficEndDate") ||
                            document.querySelector('input[name="custom.trafficEndDate"]');

                        const formatDD = (d) =>
                            d.getDate().toString().padStart(2, "0") + "/" +
                            (d.getMonth() + 1).toString().padStart(2, "0") + "/" +
                            d.getFullYear();

                        if (trafficStartEl) {
                            trafficStartEl.value = formatDD(singleDate);
                        }
                        if (trafficEndEl) {
                            trafficEndEl.value = formatDD(endDate);
                        }
                        return;
                    }
                }
            });
        }

        dateFields.forEach(function (fieldName) {
            var el =
                document.getElementById(fieldName) ||
                document.querySelector('input[name="' + fieldName + '"]');
            // Skip dateSigned field as it should remain readonly
            if (fieldName === "dateSigned") {
                return;
            }
            // Skip ALL individual date fields that are now hidden inputs managed by range pickers
            if (
                [
                    "rentStartDate",
                    "rentEndDate",
                    "eventStartDate",
                    "eventEndDate",
                ].includes(fieldName)
            ) {
                // console.log(`Skipping flatpickr initialization for ${fieldName} - hidden field managed by range picker`);
                return;
            }
            // Suppression de la conversion spécifique eventStartDate/eventEndDate
            if (el) {
                if (el.value === "Invalid date" || el.value === "Invalid time") {
                    el.value = "";
                }
                const flatpickrConfig = {
                    dateFormat: "d-m-Y",
                    allowInput: true,
                    locale: localeFlatpickr,
                    minDate: minSelectableDate, // Enforce J+minDayBooking constraint
                    disable: disableDatesFormatted,
                    onDayCreate: function (dObj, dStr, fp, dayElem) {
                        var date = dayElem.dateObj;
                        var y = date.getFullYear();
                        var m = (date.getMonth() + 1).toString().padStart(2, "0");
                        var d = date.getDate().toString().padStart(2, "0");
                        var dateStr = y + "-" + m + "-" + d;

                        // Vérifier si c'est un jour J à J+4 désactivé
                        const currentDate = new Date(date);
                        currentDate.setHours(0, 0, 0, 0);
                        if (currentDate.getTime() < minSelectableDate.getTime()) {
                            dayElem.classList.add("flatpickr-disabled", "j-plus-disabled");
                            dayElem.title = t('jourTropTot', { n: minDayBooking });
                        }

                        // Gérer les jours partiellement réservés (seulement pour les champs de réservation)
                        if (
                            partialDates.includes(dateStr) &&
                            (fieldName === "rentStartDate" || fieldName === "rentEndDate")
                        ) {
                            dayElem.classList.add("partial-booked-text");
                            dayElem.title = "Jour partiellement réservé";
                        }
                    },
                };
                // Constraint des dates d'événement entre les dates de réservation
                if (fieldName === "eventStartDate" || fieldName === "eventEndDate") {
                    const rentStartEl =
                        document.getElementById("rentStartDate") ||
                        document.querySelector('input[name="rentStartDate"]');
                    const rentEndEl =
                        document.getElementById("rentEndDate") ||
                        document.querySelector('input[name="rentEndDate"]');
                    const rentStart = rentStartEl?.value;
                    const rentEnd = rentEndEl?.value;

                    if (!rentStart || !rentEnd) {
                        el.disabled = true;
                    } else {
                        el.disabled = false;
                        // Parse rent dates and enforce constraint
                        const rentStartDate = parseDate(rentStart);
                        const rentEndDate = parseDate(rentEnd);
                        if (rentStartDate && rentEndDate) {
                            flatpickrConfig.minDate = rentStartDate;
                            flatpickrConfig.maxDate = rentEndDate;
                        }

                        // Désactiver les jours partiellement réservés pour les dates d'événement
                        flatpickrConfig.disable = [...disableDates, ...partialDates];
                    }
                }
                if (fieldName.includes("Start") || fieldName.includes("End")) {
                    flatpickrConfig.onChange = function (
                        selectedDates,
                        dateStr,
                        instance
                    ) {
                        if (selectedDates.length > 0) {
                            const selectedDate = selectedDates[0];
                            const selectedDateStr = selectedDate.toISOString().split("T")[0];
                            let overlaps = [];
                            if (disableDates.includes(selectedDateStr)) {
                                overlaps.push("Cette date a déjà des réservations validées.");
                            }
                            if (partialDates.includes(selectedDateStr)) {
                                overlaps.push("Cette date est partiellement réservée.");
                            }
                            if (overlaps.length > 0) {
                                displayErrorUnderField(
                                    el,
                                    overlaps.map((msg) => `⚠️ ${msg}`)
                                );
                                // Only disable submit buttons, not next-step buttons for multi-step forms
                                document
                                    .querySelectorAll(
                                        'button[type="submit"], input[type="submit"], .btn-submit'
                                    )
                                    .forEach((btn) => {
                                        btn.disabled = true;
                                        btn.classList.add("disabled");
                                        btn.style.opacity = "0.5";
                                        btn.style.pointerEvents = "none";
                                    });
                                instance.clear();
                                return;
                            } else {
                                document
                                    .querySelectorAll(
                                        'button[type="submit"], input[type="submit"], .btn-submit'
                                    )
                                    .forEach((btn) => {
                                        btn.disabled = false;
                                        btn.classList.remove("disabled");
                                        btn.style.opacity = "";
                                        btn.style.pointerEvents = "";
                                    });
                            }
                            // Update hidden form fields for place.njk
                            if (
                                fieldName === "eventStartDate" ||
                                fieldName === "eventEndDate"
                            ) {
                                const hiddenStartInput = document.querySelector(
                                    'form input[name="eventStartDate"]'
                                );
                                const hiddenEndInput = document.querySelector(
                                    'form input[name="eventEndDate"]'
                                );
                                if (fieldName === "eventStartDate" && hiddenStartInput) {
                                    const startDateTimeStr = selectedDate
                                        .toISOString()
                                        .slice(0, 16);
                                    hiddenStartInput.value = startDateTimeStr;
                                    // console.log('Updated hidden eventStartDate:', startDateTimeStr);
                                }
                                if (fieldName === "eventEndDate" && hiddenEndInput) {
                                    const endDateTimeStr = selectedDate
                                        .toISOString()
                                        .slice(0, 16);
                                    hiddenEndInput.value = endDateTimeStr;
                                    // console.log('Updated hidden eventEndDate:', endDateTimeStr);
                                }
                            }
                            setTimeout(validateDateRangeOverlaps, 100);
                        }
                    };
                }
                flatpickr(el, flatpickrConfig);
            }
        });
        timeFields.forEach(function (fieldName) {
            var el =
                document.getElementById(fieldName) ||
                document.querySelector('input[name="' + fieldName + '"]');
            // Skip ALL individual time fields that are now hidden inputs managed by range pickers
            if (
                [
                    "rentStartTime",
                    "rentEndTime",
                    "eventStartTime",
                    "eventEndTime",
                ].includes(fieldName)
            ) {
                // console.log(`Skipping flatpickr initialization for ${fieldName} - hidden field managed by range picker`);
                return;
            }
            // console.log('Flatpickr hour field:', fieldName, el);
            if (el) {
                if (el.value === "Invalid date" || el.value === "Invalid time") {
                    el.value = "";
                }
                flatpickr(el, {
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: "H:i",
                    time_24hr: true,
                    allowInput: true,
                    locale: localeFlatpickr,
                });
            }
        });

        // Initialize visible time picker fields (those with time-picker class)
        const timePickerElements = document.querySelectorAll(".time-picker");
        timePickerElements.forEach(function (el) {
            // console.log('Initializing time picker for:', el.name || el.id);

            // Map visible field names to hidden field names for initial value sync
            const fieldMapping = {
                visibleRentStartTime: "rentStartTime",
                visibleRentEndTime: "rentEndTime",
                visibleEventStartTime: "eventStartTime",
                visibleEventEndTime: "eventEndTime",
            };

            // Sync initial value from hidden field if it exists
            const hiddenFieldName = fieldMapping[el.name];
            const hiddenField = hiddenFieldName
                ? document.getElementById(hiddenFieldName) ||
                  document.querySelector(`input[name="${hiddenFieldName}"]`)
                : null;
            if (
                hiddenField &&
                hiddenField.value &&
                hiddenField.value !== "Invalid time"
            ) {
                el.value = hiddenField.value;
            }

            if (el.value === "Invalid date" || el.value === "Invalid time") {
                el.value = "";
            }

            // Filet de sécurité : le champ caché est le seul envoyé au serveur, et il
            // n'était synchronisé que sur `onChange` de flatpickr. Un champ caché vide
            // ou périmé face à un champ visible qui affiche bien une heure produisait
            // le rejet « dates non valides ». On les réaligne dès l'initialisation.
            if (hiddenField && hiddenField.value !== el.value) {
                hiddenField.value = el.value;
            }

            flatpickr(el, {
                enableTime: true,
                noCalendar: true,
                dateFormat: "H:i",
                time_24hr: true,
                // Saisie clavier interdite : elle ne déclenche pas `onChange`, donc le
                // champ caché n'était jamais mis à jour (cf. filet de sécurité ci-dessus).
                allowInput: false,
                locale: localeFlatpickr,
                onChange: function (selectedDates, dateStr, instance) {
                    // Map visible field names to hidden field names
                    const fieldMapping = {
                        visibleRentStartTime: "rentStartTime",
                        visibleRentEndTime: "rentEndTime",
                        visibleEventStartTime: "eventStartTime",
                        visibleEventEndTime: "eventEndTime",
                    };

                    const hiddenFieldName = fieldMapping[el.name];
                    if (hiddenFieldName) {
                        const hiddenField =
                            document.getElementById(hiddenFieldName) ||
                            document.querySelector(`input[name="${hiddenFieldName}"]`);
                        if (hiddenField) {
                            hiddenField.value = dateStr;
                            hiddenField.dispatchEvent(new Event("change", { bubbles: true }));
                        }
                    }


                    // Trigger validation after time change
                    setTimeout(validateDateRangeOverlaps, 100);
                },
            });
        });
    }

    // Exposée globalement pour permettre une revérification au submit final
    // (cf. _booking.js). Retourne `true` si OK (pas de conflit), `false` si conflit.
    window.__validateHallBookingOverlap = validateDateRangeOverlaps;

    // Function to validate date range overlaps
    function validateDateRangeOverlaps() {
        // Skip validation if not a hall booking form (rentStartTime/rentEndTime don't exist)
        const rentStartTimeEl = document.getElementById("rentStartTime");
        const rentEndTimeEl = document.getElementById("rentEndTime");

        if (!rentStartTimeEl || !rentEndTimeEl) {
            return; // Not a hall booking form, skip validation
        }

        // Check if current rentable is Open Air / Autre (skip date overlap validation)
        let rentableId = document.querySelector('input[name="rentableId"]')?.value;
        const isOpenAirRentable = rentableId === OPEN_AIR_RENTABLE_ID;

        // Skip date overlap validation for Open Air (materials are still validated server-side)
        if (isOpenAirRentable) {
            // Enable next buttons since we don't check date overlaps for Open Air
            const bookingAppForms = document.querySelectorAll(".form-bookingapp");
            bookingAppForms.forEach((form) => {
                const nextButtons = form.querySelectorAll(".next-step");
                nextButtons.forEach((button) => {
                    button.disabled = false;
                    button.classList.remove("disabled");
                    button.style.opacity = "";
                    button.style.pointerEvents = "";
                    button.removeAttribute("title");
                    button.classList.remove("has-tooltip");
                });
            });
            return true; // No overlaps for Open Air
        }

        const rentStartField =
            document.getElementById("rentStartDate") ||
            document.querySelector('input[name="rentStartDate"]');
        const rentEndField =
            document.getElementById("rentEndDate") ||
            document.querySelector('input[name="rentEndDate"]');
        const eventStartField =
            document.getElementById("eventStartDate") ||
            document.querySelector('input[name="eventStartDate"]');
        const eventEndField =
            document.getElementById("eventEndDate") ||
            document.querySelector('input[name="eventEndDate"]');

        const errorMessages = [];
        const booked = window.bookedDays || { full: [], partial: [] };

        // Clear previous error messages
        document
            .querySelectorAll(".flatpickr-overlap-error")
            .forEach((el) => el.remove());
        clearDateFieldErrors();
        // Ajout log debug pour voir les valeurs des champs et booked
        // console.log('[DEBUG] rentStartField:', rentStartField ? rentStartField.value : null);
        // console.log('[DEBUG] rentEndField:', rentEndField ? rentEndField.value : null);
        // console.log('[DEBUG] eventStartField:', eventStartField ? eventStartField.value : null);
        // console.log('[DEBUG] eventEndField:', eventEndField ? eventEndField.value : null);
        // console.log('[DEBUG] window.bookedDays:', booked);
        let hasOverlap = false;

        // ── Cohérence de la saisie, AVANT toute recherche de chevauchement ──────
        //
        // Rien ne contrôlait qu'une fin soit postérieure à un début. Pire, une plage
        // inversée DÉSACTIVAIT le contrôle : `checkDateRangeOverlap` part du début et
        // sort immédiatement quand il est postérieur à la fin, donc « aucun conflit ».
        // Saisir 20:00 → 10:00 sur un jour déjà réservé de 14:00 à 18:00 passait sans
        // un mot, et la demande partait avec un début postérieur à sa fin.
        const bornes = (champDate, champFin, heureDebut, heureFin) => {
            if (!champDate || !champFin || !champDate.value || !champFin.value) return null;
            const debut = parseDateTimezone(`${champDate.value}T${heureDebut}:00`);
            const fin = parseDateTimezone(`${champFin.value}T${heureFin}:00`);
            return debut && fin ? { debut, fin } : null;
        };

        const periodeMontage = bornes(
            rentStartField, rentEndField, rentStartTimeEl.value, rentEndTimeEl.value
        );
        const periodeEvenement = bornes(
            eventStartField, eventEndField,
            document.getElementById('eventStartTime')?.value || '08:00',
            document.getElementById('eventEndTime')?.value || '23:00'
        );

        const champMontageVisible =
            document.getElementById('rentDateRange') ||
            document.querySelector('input[name="rentDateRange"]') ||
            rentEndField;
        const champEvenementVisible =
            document.getElementById('eventDateRange') ||
            document.querySelector('input[name="eventDateRange"]') ||
            eventEndField;

        let incoherent = false;

        [[periodeMontage, champMontageVisible], [periodeEvenement, champEvenementVisible]]
            .forEach(([periode, champ]) => {
                if (!periode || periode.fin > periode.debut) return;
                incoherent = true;
                const memeJour =
                    periode.debut.getFullYear() === periode.fin.getFullYear() &&
                    periode.debut.getMonth() === periode.fin.getMonth() &&
                    periode.debut.getDate() === periode.fin.getDate();
                displayErrorUnderField(champ, [
                    memeJour ? t('finAvantDebut') : t('finAvantDebutDate'),
                ]);
            });

        // La période de montage/démontage doit ENGLOBER la manifestation : c'est elle
        // qui bloque la salle dans GMS. Saisir une manifestation les 12-13/06 et un
        // montage les 20-21/06 réservait la salle les 20-21, pas les 12-13 — la salle
        // restait donc libre le jour même de l'événement, et le contrôle de stock du
        // matériel portait lui aussi sur la mauvaise fenêtre. La contrainte existait
        // dans le code (minDate/maxDate croisés) mais dans une boucle qui ne s'exécute
        // plus jamais, `dateFields` ne portant plus que « dateSigned ».
        if (!incoherent && periodeMontage && periodeEvenement) {
            const messages = [];
            if (periodeMontage.debut > periodeEvenement.debut) messages.push(t('montageTropTard'));
            if (periodeMontage.fin < periodeEvenement.fin) messages.push(t('montageTropTot'));
            if (messages.length) {
                incoherent = true;
                displayErrorUnderField(champMontageVisible, messages);
            }
        }

        if (incoherent) {
            // Même verrouillage que pour un chevauchement : le bouton « Suivant » des
            // étapes 2 et 3 se désactive plus bas sur ce seul drapeau.
            hasOverlap = true;
        }

        // Check rent dates
        if (!incoherent && 
            rentStartField &&
            rentEndField &&
            rentStartField.value &&
            rentEndField.value
        ) {
            // const rentStart = parseDate(rentStartField.value);
            // const rentEnd = parseDate(rentEndField.value);
            const rentStartTime = rentStartTimeEl.value;
            const rentEndTime = rentEndTimeEl.value;
            const rentStart = parseDateTimezone(
                rentStartField.value + "T" + rentStartTime + ":00"
            );
            const rentEnd = parseDateTimezone(
                rentEndField.value + "T" + rentEndTime + ":00"
            );
            if (rentStart && rentEnd) {
                const overlapDates = checkDateRangeOverlap(rentStart, rentEnd, booked);
                if (overlapDates.length > 0) {
                    hasOverlap = true;
                    // Regroupement amélioré : regroupe si la différence entre la fin d'un jour et le début du suivant est ≤ 1h
                    const partials = overlapDates.filter((obj) => obj.type === "partial");
                    let grouped = [];
                    let currentGroup = [];
                    for (let i = 0; i < partials.length; i++) {
                        const curr = partials[i];
                        if (currentGroup.length === 0) {
                            currentGroup.push(curr);
                        } else {
                            const prev = currentGroup[currentGroup.length - 1];
                            // `.date` vient de checkDateRangeOverlap() : c'est du DD-MM-YYYY
                            // (format d'affichage). Il faut donc le reformater en ISO avant de
                            // l'interpoler dans un horodatage, sinon `new Date()` est invalide.
                            const prevDate = parseDate(prev.date);
                            const currDate = parseDate(curr.date);
                            let hourGap = null;
                            if (prevDate && currDate) {
                                const prevEnd = new Date(
                                    `${formatDateISO(prevDate)}T${prev.endTime}`
                                );
                                const currStart = new Date(
                                    `${formatDateISO(currDate)}T${curr.startTime}`
                                );
                                const dayDiff =
                                    (currDate - prevDate) / (1000 * 60 * 60 * 24);
                                if (dayDiff === 0 || dayDiff === 1) {
                                    hourGap = (currStart - prevEnd) / (1000 * 60 * 60);
                                }
                            }
                            if (hourGap !== null && hourGap <= 1) {
                                currentGroup.push(curr);
                            } else {
                                grouped.push([...currentGroup]);
                                currentGroup = [curr];
                            }
                        }
                    }
                    if (currentGroup.length > 0) grouped.push([...currentGroup]);
                    // Affichage des groupes
                    const messages = grouped.map((group) => {
                        const start = group[0];
                        const end = group[group.length - 1];
                        return t('conflitPlage', { debut: start.date, heureDebut: start.startTime, fin: end.date, heureFin: end.endTime });
                    });
                    // Ajoute aussi les jours "full"
                    messages.push(
                        ...overlapDates
                            .filter((obj) => obj.type === "full")
                            .map(
                                (obj) => t('conflitJour', { date: obj.date })
                            )
                    );

                    // Display error under the visible range picker field instead of hidden field
                    const rentRangeField =
                        document.getElementById("rentDateRange") ||
                        document.querySelector('input[name="rentDateRange"]');
                    displayErrorUnderField(rentRangeField || rentEndField, [
                        t('conflitIntro'),
                        ...messages,
                    ]);
                }
            }
        }
        // Check event dates
        if (
            eventStartField &&
            eventEndField &&
            eventStartField.value &&
            eventEndField.value
        ) {
            // const eventStart = parseDate(eventStartField.value);
            // const eventEnd = parseDate(eventEndField.value);

            const eventStartTimeEl = document.getElementById("eventStartTime");
            const eventEndTimeEl = document.getElementById("eventEndTime");
            const eventStartTime = eventStartTimeEl ? eventStartTimeEl.value : "00:01";
            const eventEndTime = eventEndTimeEl ? eventEndTimeEl.value : "23:59";

            const eventStart = parseDateTimezone(
                eventStartField.value + "T" + eventStartTime + ":00"
            );
            const eventEnd = parseDateTimezone(
                eventEndField.value + "T" + eventEndTime + ":00"
            );

            if (eventStart && eventEnd) {
                const overlapDates = checkDateRangeOverlap(
                    eventStart,
                    eventEnd,
                    booked
                );
                if (overlapDates.length > 0) {
                    hasOverlap = true;
                    const messages = [];
                    // Regroupement partiel
                    const partials = overlapDates.filter((obj) => obj.type === "partial");
                    let grouped = [];
                    let currentGroup = [];
                    for (let i = 0; i < partials.length; i++) {
                        const curr = partials[i];
                        if (currentGroup.length === 0) {
                            currentGroup.push(curr);
                        } else {
                            const prev = currentGroup[currentGroup.length - 1];
                            // `.date` vient de checkDateRangeOverlap() : c'est du DD-MM-YYYY
                            // (format d'affichage). Il faut donc le reformater en ISO avant de
                            // l'interpoler dans un horodatage, sinon `new Date()` est invalide.
                            const prevDate = parseDate(prev.date);
                            const currDate = parseDate(curr.date);
                            let hourGap = null;
                            if (prevDate && currDate) {
                                const prevEnd = new Date(
                                    `${formatDateISO(prevDate)}T${prev.endTime}`
                                );
                                const currStart = new Date(
                                    `${formatDateISO(currDate)}T${curr.startTime}`
                                );
                                const dayDiff =
                                    (currDate - prevDate) / (1000 * 60 * 60 * 24);
                                if (dayDiff === 0 || dayDiff === 1) {
                                    hourGap = (currStart - prevEnd) / (1000 * 60 * 60);
                                }
                            }
                            if (hourGap !== null && hourGap <= 1) {
                                currentGroup.push(curr);
                            } else {
                                grouped.push([...currentGroup]);
                                currentGroup = [curr];
                            }
                        }
                    }
                    if (currentGroup.length > 0) grouped.push([...currentGroup]);
                    messages.push(
                        ...grouped.map((group) => {
                            const start = group[0];
                            const end = group[group.length - 1];
                            return t('conflitPlage', { debut: start.date, heureDebut: start.startTime, fin: end.date, heureFin: end.endTime });
                        })
                    );
                    // Ajoute aussi les jours "full"
                    messages.push(
                        ...overlapDates
                            .filter((obj) => obj.type === "full")
                            .map(
                                (obj) => t('conflitJour', { date: obj.date })
                            )
                    );

                    // Display error under the visible range picker field instead of hidden field
                    const eventRangeField =
                        document.getElementById("eventDateRange") ||
                        document.querySelector('input[name="eventDateRange"]');
                    displayErrorUnderField(eventRangeField || eventEndField, [
                        t('conflitIntro'),
                        ...messages,
                    ]);
                }
            }
        }

        // Disable/enable next buttons based on overlap status
        // Scope aux étapes 2 ET 3 — depuis la réorganisation, les dates d'événement
        // restent en étape 2 mais la période de montage/démontage est passée en
        // étape 3. Toujours PAS l'étape 1 : y désactiver « Suivant » enfermerait
        // l'usager avant même d'atteindre les pickers après un re-rendu serveur
        // (dates de montage effacées, dates d'événement réhydratées et possiblement
        // en conflit).
        const bookingAppForms = document.querySelectorAll(".form-bookingapp");
        bookingAppForms.forEach((form) => {
            const nextButtons = form.querySelectorAll(
                '.step[data-step="2"] .next-step, .step[data-step="3"] .next-step'
            );
            nextButtons.forEach((button) => {
                if (hasOverlap) {
                    button.disabled = true;
                    button.classList.add("disabled");
                    button.style.opacity = "0.5";
                    // Info-bulle expliquant POURQUOI le bouton est désactivé. Elle
                    // passait par un `#overlapTooltip` que le gabarit ne rend plus
                    // depuis longtemps : seul le « repli » écrit en dur en français
                    // était réellement affiché, y compris sur /lu/. Une seule source
                    // désormais, traduite comme le reste. Le message de fond, lui, est
                    // sous le champ de dates et porte `role="alert"` : un bouton
                    // désactivé sort du parcours clavier, l'info-bulle ne peut donc pas
                    // être le seul porteur de l'explication.
                    button.setAttribute("title", t('conflitBouton'));
                    button.classList.add("has-tooltip");
                    // `pointer-events` maintenu : sans lui, le navigateur n'affiche pas
                    // l'info-bulle native d'un bouton désactivé.
                    button.style.pointerEvents = "auto";
                } else {
                    button.disabled = false;
                    button.classList.remove("disabled");
                    button.style.opacity = "";
                    button.style.pointerEvents = "";
                    // Suppression du tooltip si présent
                    button.removeAttribute("title");
                    button.classList.remove("has-tooltip");
                }
            });
        });
        // console.log('Date validation result:', hasOverlap ? 'HAS OVERLAPS' : 'NO OVERLAPS', errorMessages);
        return !hasOverlap;
    }
    /**
     * Affiche un ou plusieurs messages sous le champ de date VISIBLE.
     *
     * Le message existait déjà, mais restait muet pour tout ce qui n'est pas un
     * regard : pas de `role="alert"`, donc aucune annonce quand il apparaît ; pas
     * d'`aria-describedby`, donc rien qui le relie au champ ; et le champ lui-même
     * n'était pas marqué en erreur. Une personne au lecteur d'écran ne savait ni
     * que sa saisie posait problème, ni pourquoi le bouton « Suivant » venait de
     * se désactiver.
     */
    function displayErrorUnderField(field, messages) {
        if (!field) return;

        const existingError = field.parentNode.querySelector(".flatpickr-overlap-error");
        if (existingError) existingError.remove();

        const errorDiv = document.createElement("div");
        errorDiv.className = "flatpickr-overlap-error alert alert-danger";
        errorDiv.setAttribute("role", "alert");
        errorDiv.id =
            "overlap-" + String(field.name || field.id || "date").replace(/[^A-Za-z0-9_-]/g, "-");

        const list = document.createElement("ul");
        list.className = "flatpickr-overlap-error__list";
        (Array.isArray(messages) ? messages : [messages]).forEach((msg) => {
            const item = document.createElement("li");
            item.textContent = msg;
            list.appendChild(item);
        });
        errorDiv.appendChild(list);
        field.parentNode.appendChild(errorDiv);

        field.classList.add("is-invalid");
        field.setAttribute("aria-invalid", "true");
        field.setAttribute("aria-describedby", errorDiv.id);
    }

    /**
     * Retire la marque d'erreur des champs de date. Symétrique de la fonction
     * ci-dessus : sans elle, `is-invalid` et `aria-invalid` survivaient à la
     * correction, et le champ restait rouge une fois le conflit résolu.
     */
    function clearDateFieldErrors() {
        document
            .querySelectorAll(
                'input[name="rentDateRange"], input[name="eventDateRange"], input[name="trafficDateRange"]'
            )
            .forEach((el) => {
                el.classList.remove("is-invalid");
                el.removeAttribute("aria-invalid");
                el.removeAttribute("aria-describedby");
            });
    }

    // Deux formats de date cohabitent dans ce fichier, et il ne faut pas les confondre :
    //
    //  - YYYY-MM-DD : le format CANONIQUE des champs cachés envoyés au serveur
    //    (rent/eventStartDate, rent/eventEndDate). C'est le seul que le serveur accepte,
    //    et celui que le template pré-remplit (form-booking-app.njk : `| date('YYYY-MM-DD')`).
    //    formatDateISO en est le seul writer, parseISODate le seul reader.
    //
    //  - DD-MM-YYYY : un format d'AFFICHAGE, produit par formatDMY() dans bookingSlots.js
    //    pour les messages de conflit. Les créneaux renvoyés par checkDateRangeOverlap()
    //    portent des dates dans ce format — jamais en ISO.
    //
    // parseDate() reste donc volontairement tolérante aux deux : elle est appelée aussi bien
    // sur les champs cachés que sur les dates de créneaux en conflit.

    const formatDateISO = (d) =>
        d.getFullYear() +
        "-" +
        (d.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        d.getDate().toString().padStart(2, "0");

    // Lit une date YYYY-MM-DD en date LOCALE. `new Date("2026-11-27")` donnerait minuit UTC,
    // soit 01:00 ou 02:00 locale à Luxembourg : le test « minuit ? » des pickers ne se
    // déclencherait plus et les heures par défaut seraient écrasées par le décalage horaire.
    // Renvoie null si la valeur est absente ou non parsable — une construction Date() naïve
    // accepterait des valeurs absurdes sans lever d'erreur.
    function parseISODate(value) {
        if (!value) return null;
        const parts = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!parts) return null;
        const d = new Date(
            parseInt(parts[1], 10),
            parseInt(parts[2], 10) - 1,
            parseInt(parts[3], 10)
        );
        return isNaN(d.getTime()) ? null : d;
    }

    function parseDate(dateStr) {
        if (!dateStr || dateStr.trim() === "") return null;

        // YYYY-MM-DD (champs cachés)
        const isoDate = parseISODate(dateStr);
        if (isoDate) return isoDate;

        // DD-MM-YYYY (dates d'affichage des créneaux en conflit)
        if (dateStr.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
            const parts = dateStr.split("-");
            if (parts.length === 3) {
                const date = new Date(
                    parseInt(parts[2]),
                    parseInt(parts[1]) - 1,
                    parseInt(parts[0])
                );
                if (!isNaN(date.getTime())) {
                    return date;
                }
            }
        }

        return null;
    }
    // Rien à initialiser s'il n'y a aucun champ date/heure sur la page. Sans ce
    // garde-fou, la fiche salle — qui n'a que le calendrier — déclenchait quand même
    // le chargement de tout le domaine sélectionnable (18 mois), simplement parce que
    // `findRentableId()` trouve le `data-rentableid` du calendrier.
    const hasDateFields = document.querySelector(
        '.date-range-picker, .time-picker, input[name="rentStartDate"], input[name="eventStartDate"]'
    );
    if (!hasDateFields) return;

    // Afficher le chargement dès le début
    showFlatpickrLoading();

    loadFlatpickr(function () {
        fetchBookedDays(function (booked) {
            window.bookedDays = booked;
            initFlatpickrFieldsWithBookedDays(booked);

            // Notify FullCalendar that booking data is ready
            window.dispatchEvent(
                new CustomEvent("bookedDaysReady", {
                    detail: { bookedDays: booked },
                })
            );

            // Masquer le chargement une fois terminé
            setTimeout(hideFlatpickrLoading, 200);

            // Set up date validation listeners
            const isAppointmentForm =
                document.querySelector("form.js-form-push") !== null;
            let allFields = [
                "rentStartDate",
                "rentEndDate",
                "rentStartTime",
                "rentEndTime",
            ];

            // Event fields are now hidden inputs, no need to add them to validation

            allFields.forEach((fieldName) => {
                const field =
                    document.getElementById(fieldName) ||
                    document.querySelector(`input[name="${fieldName}"]`);
                if (field) {
                    field.addEventListener("change", () => {
                        validateDateRangeOverlaps();
                        setTimeout(validateRequiredFields, 100);
                    });
                    field.addEventListener("blur", () => {
                        validateDateRangeOverlaps();
                        setTimeout(validateRequiredFields, 100);
                    });
                    field.addEventListener("input", () => {
                        setTimeout(validateRequiredFields, 100);
                    });
                }
            });

            // Initial validation
            setTimeout(() => {
                validateDateRangeOverlaps();
                validateRequiredFields();
            }, 500);

            // Event fields are now hidden inputs - no need to disable them

            // console.log('Flatpickr initialization completed');
        });
    });

    // Fonction pour vérifier si un champ doit être désactivé selon son placeholder
    function shouldDisableEventField(el) {
        if (!el) return false;
        const placeholder = el.placeholder || el.getAttribute("placeholder");
        return (
            placeholder &&
            placeholder.includes("Sélectionnez d'abord les dates de réservation")
        );
    }

    // Function simplified - event fields are now hidden inputs managed by range picker
    function updateEventFlatpickr() {
        // No longer needed since event fields are hidden inputs
        // The eventDateRange picker handles all event date/time logic
        return;
    }

    // Helper function to check if rent dates have conflicts
    function hasRentDateConflicts() {
        // Check if current rentable is Open Air / Autre (no date conflicts check)
        let rentableId = document.querySelector('input[name="rentableId"]')?.value;
        const isOpenAirRentable = rentableId === OPEN_AIR_RENTABLE_ID;

        // Skip date conflict validation for Open Air
        if (isOpenAirRentable) {
            return false;
        }

        const rentStartEl =
            document.getElementById("rentStartDate") ||
            document.querySelector('input[name="rentStartDate"]');
        const rentEndEl =
            document.getElementById("rentEndDate") ||
            document.querySelector('input[name="rentEndDate"]');

        if (!rentStartEl?.value || !rentEndEl?.value) return false;

        const rentStart = parseDate(rentStartEl.value);
        const rentEnd = parseDate(rentEndEl.value);

        if (!rentStart || !rentEnd) return false;

        const booked = window.bookedDays || { full: [], partial: [] };
        const overlapDates = checkDateRangeOverlap(rentStart, rentEnd, booked);

        return overlapDates.length > 0;
    }

    // Function to validate all required fields and enable/disable submit buttons
    function validateRequiredFields() {
        const isAppointmentForm =
            document.querySelector("form.js-form-push") !== null;
        let requiredFields = [
            "rentStartDate",
            "rentEndDate",
            "rentStartTime",
            "rentEndTime",
        ];

        // Event fields are now hidden inputs, no need to validate them manually

        let allFieldsValid = true;

        // Check each required field
        requiredFields.forEach((fieldName) => {
            const field =
                document.getElementById(fieldName) ||
                document.querySelector(`input[name="${fieldName}"]`);
            if (field && !field.disabled) {
                if (
                    !field.value ||
                    field.value.trim() === "" ||
                    field.value === "Invalid date" ||
                    field.value === "Invalid time"
                ) {
                    allFieldsValid = false;
                    // console.log(`Required field ${fieldName} is empty or invalid:`, field.value);
                }
            }
        });

        // Also check if there are any date conflicts
        const hasConflicts = hasRentDateConflicts();
        if (hasConflicts) {
            allFieldsValid = false;
            // console.log('Date conflicts detected, keeping submit disabled');
        }

        // Enable/disable submit buttons
        const formBookingApp = document.querySelector(".form-bookingapp");

        return allFieldsValid;
    }

    // Ajout : listeners dynamiques sur les champs de réservation
    var rentStartEl =
        document.getElementById("rentStartDate") ||
        document.querySelector('input[name="rentStartDate"]');
    var rentEndEl =
        document.getElementById("rentEndDate") ||
        document.querySelector('input[name="rentEndDate"]');
    if (rentStartEl) rentStartEl.addEventListener("change", updateEventFlatpickr);
    if (rentEndEl) rentEndEl.addEventListener("change", updateEventFlatpickr);
    // Initialisation à l'ouverture
    updateEventFlatpickr();
});
